// Modos de dificuldade do Capoeira Soul.
//
// Toda a calibragem vive aqui — os sistemas de combate, IA, scoring e cura
// consomem `getDifficultyConfig()` (ou helpers derivados) em um único ponto
// de entrada. Para rebalancear o jogo, edite apenas o objeto PRESETS abaixo.

export type Difficulty = 'easy' | 'medium' | 'hard'

export interface DifficultyConfig {
  /** Multiplica o HP inicial de Enemy/Boss. */
  enemyHpMultiplier: number
  /** Multiplica o dano que inimigos infligem no jogador. */
  enemyDamageMultiplier: number
  /** Multiplica o dano do jogador contra inimigos/bosses. */
  playerDamageMultiplier: number
  /** Multiplica o nível de agressão da IA (cooldown e prontidão). */
  enemyAggroMultiplier: number
  /** Soma (em ms) na janela de telegraph dos ataques inimigos. Positivo = mais lento (mais fácil). */
  telegraphMsBonus: number
  /** Multiplica a frequência de ondas (>1 = ondas mais rápidas). */
  waveSpawnRateMultiplier: number
  /** Multiplica o ganho de Axé. */
  axeGainMultiplier: number
  /** Multiplica o HP recuperado pelo `Player.heal()`. */
  healingEffectMultiplier: number
  /** Multiplica a tolerância dos limiares de S/A/B (>1 = mais fácil tirar S). */
  scoreRankToleranceMultiplier: number
}

export const DIFFICULTY_PRESETS: Record<Difficulty, DifficultyConfig> = {
  easy: {
    enemyHpMultiplier: 0.80,
    enemyDamageMultiplier: 0.85,
    playerDamageMultiplier: 1.10,
    enemyAggroMultiplier: 0.90,
    telegraphMsBonus: 180,
    waveSpawnRateMultiplier: 0.90,
    axeGainMultiplier: 1.20,
    healingEffectMultiplier: 1.15,
    scoreRankToleranceMultiplier: 1.15,
  },
  medium: {
    enemyHpMultiplier: 1.00,
    enemyDamageMultiplier: 1.00,
    playerDamageMultiplier: 1.00,
    enemyAggroMultiplier: 1.00,
    telegraphMsBonus: 0,
    waveSpawnRateMultiplier: 1.00,
    axeGainMultiplier: 1.00,
    healingEffectMultiplier: 1.00,
    scoreRankToleranceMultiplier: 1.00,
  },
  hard: {
    enemyHpMultiplier: 1.20,
    enemyDamageMultiplier: 1.15,
    playerDamageMultiplier: 0.95,
    enemyAggroMultiplier: 1.15,
    telegraphMsBonus: -120,
    waveSpawnRateMultiplier: 1.15,
    axeGainMultiplier: 0.90,
    healingEffectMultiplier: 0.90,
    scoreRankToleranceMultiplier: 0.90,
  },
}

export const DEFAULT_DIFFICULTY: Difficulty = 'medium'

const VALID_DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard']

export function isDifficulty(value: unknown): value is Difficulty {
  return typeof value === 'string' && (VALID_DIFFICULTIES as readonly string[]).includes(value)
}

/** Resolve o preset ativo, com fallback seguro para `medium` em saves antigos. */
export function getDifficultyConfig(d?: Difficulty | string | null): DifficultyConfig {
  return DIFFICULTY_PRESETS[isDifficulty(d) ? d : DEFAULT_DIFFICULTY]
}

/** Aplica HP e dano base do inimigo/boss segundo a dificuldade. */
export function applyDifficultyToEnemyStats(
  base: { hp: number; damage: number },
  d?: Difficulty,
): { hp: number; damage: number } {
  const cfg = getDifficultyConfig(d)
  return {
    hp: Math.max(1, Math.round(base.hp * cfg.enemyHpMultiplier)),
    damage: Math.max(1, Math.round(base.damage * cfg.enemyDamageMultiplier)),
  }
}

/** Converte o bônus de telegraph (ms) em frames @ 60fps e soma à janela base. */
export function getTelegraphFrames(baseFrames: number, d?: Difficulty): number {
  const cfg = getDifficultyConfig(d)
  const bonusFrames = Math.round((cfg.telegraphMsBonus / 1000) * 60)
  return Math.max(6, baseFrames + bonusFrames)
}

// ============================================================================
// Singleton de dificuldade ativa.
//
// Acessado por cenas Phaser que recebem a difficulty via `init(payload)` e
// querem disponibilizá-la a sistemas singleton (AxeSystem, Player.heal etc.)
// sem ter que passar como parâmetro de construtor em toda a cadeia.
// ============================================================================

let active: Difficulty = DEFAULT_DIFFICULTY

export const CurrentDifficulty = {
  get(): Difficulty {
    return active
  },
  set(d?: Difficulty | string | null): void {
    active = isDifficulty(d) ? d : DEFAULT_DIFFICULTY
  },
  config(): DifficultyConfig {
    return DIFFICULTY_PRESETS[active]
  },
}
